import { useEffect, useState } from 'react';
import { CalendarDays, Paperclip, Send, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { useI18n } from '../lib/i18n';
import { REQUEST_VISIT_TYPES } from '../lib/visitType';
import { FileDrop } from './FileDrop';

/**
 * Botones "Request: Inspection / ATR / Reinspection / Check pickup / Material sample / Reschedule".
 * NINGUNO se manda al instante: cada uno DESPLIEGA un formulario para dejar notas (que quedan como
 * las notas para el inspector en el inspector app) + arrastrar fotos/archivos (se adjuntan al claim).
 * Al enviar, manda la COPIA del claim a Scheduling → "Waiting to Schedule" con el sub-estatus del
 * tipo. Para ATR además se crea un To-Do COMPARTIDO al equipo de Scheduling. office+.
 */
export function RequestVisitButtons({ claimId, onDone, compact, openRequest }: {
  claimId: string; onDone?: () => void; compact?: boolean;
  /** Señal externa (ej. el dropdown "Request scheduling…" del header): abre el formulario de ESE tipo. */
  openRequest?: { type: string; n: number } | null;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Tipo cuyo formulario está desplegado (notas + archivos) antes de enviar.
  const [openType, setOpenType] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // El dropdown de arriba ya NO manda el request directo (creaba ATRs sin instrucciones y por
  // duplicado): ahora solo ABRE este formulario para dejar las notas y enviar explícito.
  useEffect(() => {
    if (openRequest?.type) { setOpenType(openRequest.type); setNotes(''); setFiles([]); setMsg(null); }
  }, [openRequest?.n, openRequest?.type]);

  function toggle(type: string) {
    setOpenType((o) => (o === type ? null : type));
    setNotes(''); setFiles([]); setMsg(null);
  }

  async function submit(type: string, label: string) {
    setBusy(type); setMsg(null);
    try {
      // Sube los archivos a R2 (en paralelo) y arma las llaves para adjuntarlos.
      const fileKeys = await Promise.all(files.map(async (f) => ({ key: await uploadFile(f, 'misc'), name: f.name, mime: f.type || null })));
      await api.post(`/api/claims/${claimId}/request-visit`, { type, notes: notes.trim() || undefined, fileKeys });
      setMsg({ ok: true, text: `${label} → ${t('req.sent')}` });
      setOpenType(null); setNotes(''); setFiles([]);
      onDone?.();
    } catch (e) {
      const m = e instanceof ApiError ? e.message : 'Error';
      setMsg({ ok: false, text: m === 'duplicate_request' ? t('req.duplicate').replace('{type}', label) : m });
    } finally { setBusy(''); }
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {!compact && <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><CalendarDays size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('req.title')}</span>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {REQUEST_VISIT_TYPES.map((v) => {
          const label = t(v.labelKey);
          const active = openType === v.type;
          return (
            <button key={v.type} className={`btn ghost sm${active ? ' active' : ''}`} disabled={!!busy} onClick={() => toggle(v.type)}
              style={{ fontSize: 'var(--fs-2xs)', ...(active ? { background: 'var(--accent-soft)', borderColor: 'var(--accent-2)' } : {}) }} title={t('req.sendHint')}>
              {busy === v.type ? '…' : <><v.icon size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {label}</>}
            </button>
          );
        })}
      </div>

      {/* Formulario desplegado del tipo elegido: notas para el inspector + fotos/archivos. */}
      {openType && (() => {
        const v = REQUEST_VISIT_TYPES.find((x) => x.type === openType)!;
        const label = t(v.labelKey);
        return (
          <div className="card" style={{ display: 'grid', gap: 8, borderColor: 'var(--accent-2)' }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('req.visitHint').replace('{type}', label)}</span>
            <textarea className="input" rows={4} placeholder={t('req.atrNotesPh')} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <FileDrop
              multiple
              accept="image/*,application/pdf"
              label={t('req.atrFiles')}
              onFile={(f) => setFiles((fs) => [...fs, f])}
              onFiles={(fs) => setFiles((cur) => [...cur, ...fs])}
            />
            {files.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {files.map((f, i) => (
                  <span key={i} className="badge" style={{ background: 'var(--surface-2)', fontSize: 'var(--fs-2xs)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <Paperclip size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {f.name}
                    <button className="btn ghost sm" style={{ padding: '0 4px' }} onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}><X size={12} strokeWidth={1.75} /></button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn sm" disabled={busy === openType} onClick={() => submit(v.type, label)}>{busy === openType ? '…' : <><Send size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('req.sendVisit').replace('{type}', label)}</>}</button>
              <button className="btn ghost sm" disabled={busy === openType} onClick={() => { setOpenType(null); setNotes(''); setFiles([]); }}>{t('common.cancel')}</button>
            </div>
          </div>
        );
      })()}

      {msg && <div style={{ fontSize: 'var(--fs-2xs)', color: msg.ok ? 'var(--green)' : 'var(--red)', fontWeight: 'var(--fw-semibold)' }}>{msg.text}</div>}
    </div>
  );
}
